import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { SaveAsRounded } from '@mui/icons-material';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { PostsByIdsDocument } from '@/graphql/generated/posts-by-ids.generated';
import { useAndie } from '@/providers/andie-provider';
import { useSnackbar } from 'notistack';

type ActionResponse = ActionWorkflowAi<{
    postIds: string[];
}>;

type ActionArgs = {
    planningVariableId: string;
};

export type AndieSavePlanningAndGenerateBriefingsProps = ActionRenderAi<ActionResponse, ActionArgs>;

const AndieSavePlanningAndGenerateBriefings = (props: AndieSavePlanningAndGenerateBriefingsProps) => {
    const { t } = useTranslation();
    const { helpers } = useAndie();
    const { enqueueSnackbar } = useSnackbar();
    const executingSteps = useRef<number>(0);
    const [postIds, setPostIds] = useState<string[]>([]);

    useQuery(PostsByIdsDocument, {
        skip: postIds.length === 0,
        variables: { ids: postIds },
        onCompleted: (data) => {
            data?.postsByIds?.forEach((post) => helpers.addPost(post));
        },
        onError: (e) => {
            enqueueSnackbar(e.message);
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (props.status === ActionResponseStatusAi.EXECUTING) {
            executingSteps.current += 1;
        }

        if (
            props.status === ActionResponseStatusAi.COMPLETE &&
            props.result?.result?.postIds?.length &&
            executingSteps.current > 0
        ) {
            setPostIds(props.result.result.postIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.status]);

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.save-planning-briefings.title')}
            icon={<SaveAsRounded fontSize="small" />}
            loadingMessages={() => [t('andie.tool.save-planning-briefings.loading')]}
            completeMessage={({ result }) => {
                return t('andie.tool.save-planning-briefings.complete', {
                    value: (result?.result && result?.result.postIds.length) || 0,
                });
            }}
            {...props}
        />
    );
};

export default AndieSavePlanningAndGenerateBriefings;
